/**
 * Calendar reporting periods, year-agnostic. Browser/server copy of the
 * backend's `services/periods.js` (itself ported from canideal.org
 * lib/periods.js): no hardcoded year — a period is valid from `minYear` (a
 * data constant, default REPORTING_SCOPE_START_YEAR) up to one year past the
 * reference clock, and indices are monotonic across years (2027 Q1 > 2026 Q4).
 */

export const DAY_MS = 24 * 60 * 60 * 1000;

/** Governed public reporting begins with the 2026 programme (data scope, not a clock). */
export const REPORTING_SCOPE_START_YEAR = 2026;

export type PeriodFrequency = 'month' | 'quarter' | 'day';

export interface PeriodOptions {
  /** Earliest accepted year (default REPORTING_SCOPE_START_YEAR). */
  minYear?: number;
  /** Reference clock (ms or date); the latest accepted year is its year + 1. */
  now?: number | string | Date;
  frequencies?: PeriodFrequency[];
}

export interface ParsedPeriod {
  value: string;
  year: number;
  /** Month (1–12) or quarter (1–4); month for day periods. */
  number: number;
  frequency: PeriodFrequency;
  /** year*12+month-1, year*4+quarter-1 or days since epoch. */
  index: number;
  /** UTC midnight of the period's final day. */
  lastDay: number;
  /** First millisecond after the period. */
  next: number;
}

const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;
const QUARTER = /^(\d{4})-Q([1-4])$/;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function referenceYear(now: PeriodOptions['now']): number {
  const at = now === undefined ? Date.now() : +new Date(now);
  return new Date(Number.isFinite(at) ? at : Date.now()).getUTCFullYear();
}

/** True when `year` is within [minYear, reference year + 1]. */
export function yearInScope(
  year: number,
  { minYear = REPORTING_SCOPE_START_YEAR, now }: PeriodOptions = {}
): boolean {
  return Number.isInteger(year) && year >= minYear && year <= referenceYear(now) + 1;
}

function calendarDay(year: number, month: number, day: number): number | null {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return month >= 1 && month <= 12 && day >= 1 && day <= last
    ? Date.UTC(year, month - 1, day)
    : null;
}

/** 'YYYY-MM' | 'YYYY-Qn' | 'YYYY-MM-DD' → period description, or null. */
export function parsePeriod(value: unknown, options: PeriodOptions = {}): ParsedPeriod | null {
  if (typeof value !== 'string') return null;
  const { frequencies = ['month', 'quarter'] } = options;
  let match: RegExpExecArray | null;
  let parsed: Omit<ParsedPeriod, 'value' | 'next'> | null = null;
  if (frequencies.includes('month') && (match = MONTH.exec(value))) {
    const year = Number(match[1]);
    const number = Number(match[2]);
    parsed = {
      year,
      number,
      frequency: 'month',
      index: year * 12 + number - 1,
      lastDay: Date.UTC(year, number, 0),
    };
  } else if (frequencies.includes('quarter') && (match = QUARTER.exec(value))) {
    const year = Number(match[1]);
    const number = Number(match[2]);
    parsed = {
      year,
      number,
      frequency: 'quarter',
      index: year * 4 + number - 1,
      lastDay: Date.UTC(year, number * 3, 0),
    };
  } else if (frequencies.includes('day') && (match = DATE.exec(value))) {
    const year = Number(match[1]);
    const lastDay = calendarDay(year, Number(match[2]), Number(match[3]));
    if (lastDay === null) return null;
    parsed = { year, number: Number(match[2]), frequency: 'day', index: lastDay / DAY_MS, lastDay };
  }
  if (!parsed || !yearInScope(parsed.year, options)) return null;
  return { value, ...parsed, next: parsed.lastDay + DAY_MS };
}

/** Monthly index (year*12+month-1) of a 'YYYY-MM' period in reporting scope, else null. */
export function monthIndex(value: unknown, options: Omit<PeriodOptions, 'frequencies'> = {}) {
  return parsePeriod(value, { ...options, frequencies: ['month'] })?.index ?? null;
}
